import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from './LanguageContext';
import { getTranslation } from './translations';
import LanguageSelector from './LanguageSelector';

// Separate component for back button
const BackButton = ({ currentView, onBackToSetup }) => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  
  const handleBackClick = () => {
    if (currentView === 'results') {
      // If in results view, go back to setup within the same component
      onBackToSetup();
    } else {
      // If in setup view, go back to previous page in browser history
      navigate(-1);
    }
  };
  
  return (
    <button
      onClick={handleBackClick}
      style={{
        background: 'none',
        border: 'none',
        fontSize: 24,
        cursor: 'pointer',
        color: '#888',
        padding: 8,
        borderRadius: 4,
        transition: 'color 0.2s',
        textDecoration: 'none',
      }}
      title={getTranslation('back', language)}
    >
      ←
    </button>
  );
};

// Breadcrumbs component
const Breadcrumbs = ({ currentView }) => {
  const { language } = useLanguage();
  const location = window.location;
  const segments = location.pathname.split('/').filter(Boolean);
  
  // Special cases: skip the intermediate 'mecz' segment for specific routes
  let crumbs;
  if (location.pathname.startsWith('/mecz/mecz-towarzyski')) {
    if (location.pathname === '/mecz/mecz-towarzyski/dodaj') {
      crumbs = currentView === 'results' 
        ? ['', 'mecz-towarzyski', 'dodaj-mecz', 'wynik']
        : ['', 'mecz-towarzyski', 'dodaj-mecz'];
    } else {
      crumbs = currentView === 'results' 
        ? ['', 'mecz-towarzyski', 'wynik']
        : ['', 'mecz-towarzyski'];
    }
  } else if (location.pathname === '/mecz/turniej-rankingowy') {
    crumbs = ['', 'turniej-rankingowy'];
  } else {
    crumbs = [''].concat(segments);
  }
  
  return (
    <div style={{ fontFamily: 'Nunito, Arial, sans-serif', fontWeight: 600, fontSize: window.innerWidth <= 600 ? 18 : 36, color: '#1e3a8a', textAlign: 'center', letterSpacing: 1 }}>
      {crumbs.map((seg, idx) => {
        const decodedSeg = decodeURIComponent(seg);
        let displayText;
        
        // Use lowercase version for breadcrumbs
        if (decodedSeg === 'wynik') {
          displayText = getTranslation('wynik-breadcrumb', language);
        } else {
          displayText = getTranslation(decodedSeg, language);
        }
        
        return (
          <span key={idx}>
            {idx > 0 && <span style={{ margin: '0 8px' }}>/</span>}
            {displayText}
          </span>
        );
      })}
    </div>
  );
};

const FriendlyGame = ({ userProfile }) => {
  const { language } = useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const navigate = useNavigate();

  // Get user's full name
  const getUserFullName = () => {
    if (userProfile?.firstname && userProfile?.lastname) {
      return `${userProfile.firstname} ${userProfile.lastname}`;
    }
    return '';
  };

  // Component state for team setup
  const [gameState, setGameState] = useState({
    yourTeam: {
      gameType: 'singiel', // 'singiel', 'dublet', 'triplet'
      players: [getUserFullName(), '', ''], // [gracz1, gracz2, gracz3] - first player is logged in user
      points: 0 // Points for the game
    },
    opponentTeam: {
      gameType: 'singiel', // 'singiel', 'dublet', 'triplet'
      players: ['', '', ''], // [gracz1, gracz2, gracz3]
      points: 0 // Points for the game
    },
    isGameActive: false,
    currentView: 'setup' // 'setup' or 'results'
  });

  // Handlers for team management
  const handleGameTypeChange = (team, gameType) => {
    setGameState(prev => ({
      ...prev,
      [team]: {
        ...prev[team],
        gameType: gameType,
        players: team === 'yourTeam' 
          ? [getUserFullName(), '', ''] // Preserve logged-in user's name for your team
          : ['', '', ''] // Reset players for opponent team
      }
    }));
  };

  const handlePlayerNameChange = (team, playerIndex, name) => {
    // Prevent editing the first player in "your team" (logged-in user)
    if (team === 'yourTeam' && playerIndex === 0) {
      return;
    }
    
    setGameState(prev => ({
      ...prev,
      [team]: {
        ...prev[team],
        players: prev[team].players.map((player, index) => 
          index === playerIndex ? name : player
        )
      }
    }));
  };

  const validateTeams = () => {
    const { yourTeam, opponentTeam } = gameState;
    
    // Get required players for each team based on game type
    const getRequiredPlayers = (team) => {
      const gameType = team.gameType;
      if (gameType === 'singiel') return 1;
      if (gameType === 'dublet') return 2;
      if (gameType === 'triplet') return 3;
      return 0;
    };
    
    const yourTeamRequired = getRequiredPlayers(yourTeam);
    const opponentTeamRequired = getRequiredPlayers(opponentTeam);
    
    // Check if all required players have at least 3 characters
    const validateTeamPlayers = (team, requiredCount) => {
      for (let i = 0; i < requiredCount; i++) {
        if (!team.players[i] || team.players[i].trim().length < 3) {
          return false;
        }
      }
      return true;
    };
    
    const yourTeamValid = validateTeamPlayers(yourTeam, yourTeamRequired);
    const opponentTeamValid = validateTeamPlayers(opponentTeam, opponentTeamRequired);
    
    return yourTeamValid && opponentTeamValid;
  };

  const handleStartGame = () => {
    console.log('Starting friendly game with teams:', gameState);
    
    // Validate teams before proceeding
    if (!validateTeams()) {
      setError(getTranslation('wszyscyGraczeMin3Znaki', language));
      return;
    }
    
    // Clear any previous errors
    setError(null);
    
    // Switch to results view
    setGameState(prev => ({
      ...prev,
      currentView: 'results',
      isGameActive: true
    }));
  };

  const handleBackToSetup = () => {
    setGameState(prev => ({
      ...prev,
      currentView: 'setup',
      isGameActive: false
    }));
    // Clear any errors when going back to setup
    setError(null);
  };

  const isPlayerFieldValid = (team, playerIndex) => {
    const gameType = team.gameType;
    const requiredCount = gameType === 'singiel' ? 1 : gameType === 'dublet' ? 2 : 3;
    
    if (playerIndex >= requiredCount) return true; // Field not required
    
    const playerName = team.players[playerIndex];
    return playerName && playerName.trim().length >= 3;
  };

  const handlePointsChange = (team, change) => {
    setGameState(prev => ({
      ...prev,
      [team]: {
        ...prev[team],
        points: Math.max(0, Math.min(13, prev[team].points + change))
      }
    }));
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    // Navigate back to friendly games menu
    navigate('/mecz/mecz-towarzyski');
  };

  const handleSaveGame = async () => {
    setIsLoading(true);
    setError(null);

    // Check if both teams have equal points (no draws allowed in petanque)
    if (gameState.yourTeam.points === gameState.opponentTeam.points) {
      setError(getTranslation('petanqueNieMaRemisow', language));
      setIsLoading(false);
      return;
    }

    try {
      // Prepare game data according to the requirements
      const gameData = {
        numberofplayers: gameState.yourTeam.gameType === 'singiel' ? 1 : 
                        gameState.yourTeam.gameType === 'dublet' ? 2 : 3,
        tournament: null, // Friendly game, not a tournament
        player1: userProfile?.id, // Will be set by backend
        player2: gameState.yourTeam.players[1] || null,
        player3: gameState.yourTeam.players[2] || null,
        opponent1: gameState.opponentTeam.players[0] || null,
        opponent2: gameState.opponentTeam.players[1] || null,
        opponent3: gameState.opponentTeam.players[2] || null,
        roundtype: 'friendly', // Friendly game
        playerscore: gameState.yourTeam.points,
        opponentscore: gameState.opponentTeam.points
      };

      // Get the token from sessionStorage (consistent with App.js)
      const token = sessionStorage.getItem('access_token');
      console.log('Token found:', token ? 'Yes' : 'No');
      if (!token) {
        throw new Error('Brak tokenu autoryzacji. Zaloguj się ponownie.');
      }

      console.log('Sending game data:', gameData);
      
      const response = await fetch('/api/games', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(gameData)
      });

      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);

      if (!response.ok) {
        const errorData = await response.json();
        console.log('Error data:', errorData);
        throw new Error(errorData.detail || 'Błąd podczas zapisywania gry');
      }

      const savedGame = await response.json();
      console.log('Game saved successfully:', savedGame);
      
      // Show success modal
      setShowSuccessModal(true);
      
    } catch (err) {
      console.error('Error saving game:', err);
      setError(err.message || getTranslation('wystapilBladZapisywania', language));
    } finally {
      setIsLoading(false);
    }
  };

  // Update first player name when user profile changes
  useEffect(() => {
    if (userProfile?.firstname && userProfile?.lastname) {
      const fullName = `${userProfile.firstname} ${userProfile.lastname}`;
      setGameState(prev => ({
        ...prev,
        yourTeam: {
          ...prev.yourTeam,
          players: [fullName, prev.yourTeam.players[1], prev.yourTeam.players[2]]
        }
      }));
    }
  }, [userProfile]);

  return (
    <div style={{ position: 'relative', minHeight: '100vh', padding: 20 }}>
      {/* Language Selector - Top Right */}
      <div style={{
        position: 'absolute',
        top: '20px',
        right: '20px',
        zIndex: 1000
      }}>
        <LanguageSelector />
      </div>

      {/* Main Title */}
      <h1 style={{
        fontSize: window.innerWidth <= 600 ? '2.5rem' : '3.5rem',
        fontWeight: 'bold',
        color: '#7c3aed',
        margin: '0 0 16px 0',
        textAlign: 'center',
        fontFamily: 'Nunito, Arial, sans-serif'
      }}>
        {getTranslation('shootMe', language)}
      </h1>
      
      {/* Back button and breadcrumbs in horizontal layout */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        gap: '20px',
        margin: '24px 0'
      }}>
        <BackButton 
          currentView={gameState.currentView} 
          onBackToSetup={handleBackToSetup} 
        />
                      <Breadcrumbs currentView={gameState.currentView} />
      </div>

      {/* Main Content Area */}
      {gameState.currentView === 'setup' ? (
        <>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'flex-start', 
          minHeight: '50vh',
          gap: '20px',
          padding: '0 20px',
          flexWrap: 'wrap'
        }}>
        {/* {getTranslation('twoja-druzyna', language)} Section */}
        <div style={{
          background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
          padding: '30px',
          borderRadius: '16px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          minWidth: '300px',
          maxWidth: '400px',
          flex: '1'
        }}>
          <h2 style={{
            fontFamily: 'Nunito, Arial, sans-serif',
            fontWeight: 600,
            fontSize: '1.5rem',
            color: '#1565c0',
            margin: '0 0 20px 0',
            textAlign: 'center'
          }}>
            {getTranslation('twojaDruzyna', language)}
          </h2>
          
          {/* Game Type Selection */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
              {['singiel', 'dublet', 'triplet'].map((type) => (
                <button
                  key={type}
                  onClick={() => handleGameTypeChange('yourTeam', type)}
                  style={{
                    background: gameState.yourTeam.gameType === type 
                      ? 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)'
                      : 'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)',
                    color: gameState.yourTeam.gameType === type ? 'white' : '#333',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '20px',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    fontFamily: 'Nunito, Arial, sans-serif',
                    textTransform: 'capitalize'
                  }}
                  onMouseOver={(e) => {
                    if (gameState.yourTeam.gameType !== type) {
                      e.target.style.background = 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (gameState.yourTeam.gameType !== type) {
                      e.target.style.background = 'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)';
                    }
                  }}
                >
                  {getTranslation(type, language)}
                </button>
              ))}
            </div>
          </div>

          {/* Player Input Fields */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            {gameState.yourTeam.players.map((player, index) => {
              const shouldShow = 
                (gameState.yourTeam.gameType === 'singiel' && index === 0) ||
                (gameState.yourTeam.gameType === 'dublet' && index < 2) ||
                (gameState.yourTeam.gameType === 'triplet');
              
              if (!shouldShow) return null;
              
              return (
                <div key={index}>
                  <label style={{
                    display: 'block',
                    fontFamily: 'Nunito, Arial, sans-serif',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    color: '#1565c0',
                    marginBottom: '5px'
                  }}>
                    {getTranslation('gracz', language)} {index + 1}:
                  </label>
                  <input
                    type="text"
                    value={player}
                    onChange={(e) => handlePlayerNameChange('yourTeam', index, e.target.value)}
                    placeholder={index === 0 ? getTranslation('zalogowanyUzytkownik', language) : `${getTranslation('wprowadzImieGracza', language)} ${index + 1}`}
                    disabled={index === 0} // Disable first player field (logged-in user)
                    maxLength={30}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: `2px solid ${
                        index === 0 
                          ? '#e3f2fd' 
                          : isPlayerFieldValid(gameState.yourTeam, index) 
                            ? '#4caf50' 
                            : '#f44336'
                      }`,
                      borderRadius: '8px',
                      fontSize: '1rem',
                      fontFamily: 'Nunito, Arial, sans-serif',
                      outline: 'none',
                      transition: 'border-color 0.2s ease',
                      boxSizing: 'border-box',
                      backgroundColor: index === 0 ? '#f5f5f5' : 'white',
                      color: index === 0 ? '#666' : '#333',
                      cursor: index === 0 ? 'not-allowed' : 'text'
                    }}
                    onFocus={(e) => {
                      if (index !== 0) {
                        e.target.style.borderColor = isPlayerFieldValid(gameState.yourTeam, index) ? '#4caf50' : '#f44336';
                      }
                    }}
                    onBlur={(e) => {
                      if (index !== 0) {
                        e.target.style.borderColor = isPlayerFieldValid(gameState.yourTeam, index) ? '#4caf50' : '#f44336';
                      }
                    }}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Przeciwnicy Section */}
        <div style={{
          background: 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)',
          padding: '30px',
          borderRadius: '16px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          minWidth: '300px',
          maxWidth: '400px',
          flex: '1'
        }}>
          <h2 style={{
            fontFamily: 'Nunito, Arial, sans-serif',
            fontWeight: 600,
            fontSize: '1.5rem',
            color: '#c62828',
            margin: '0 0 20px 0',
            textAlign: 'center'
          }}>
            {getTranslation('druzynaPrzeciwna', language)}
          </h2>
          
          {/* Game Type Selection */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
              {['singiel', 'dublet', 'triplet'].map((type) => (
                <button
                  key={type}
                  onClick={() => handleGameTypeChange('opponentTeam', type)}
                  style={{
                    background: gameState.opponentTeam.gameType === type 
                      ? 'linear-gradient(135deg, #d32f2f 0%, #c62828 100%)'
                      : 'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)',
                    color: gameState.opponentTeam.gameType === type ? 'white' : '#333',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '20px',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    fontFamily: 'Nunito, Arial, sans-serif',
                    textTransform: 'capitalize'
                  }}
                  onMouseOver={(e) => {
                    if (gameState.opponentTeam.gameType !== type) {
                      e.target.style.background = 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (gameState.opponentTeam.gameType !== type) {
                      e.target.style.background = 'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)';
                    }
                  }}
                >
                  {getTranslation(type, language)}
                </button>
              ))}
            </div>
          </div>

          {/* Player Input Fields */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            {gameState.opponentTeam.players.map((player, index) => {
              const shouldShow = 
                (gameState.opponentTeam.gameType === 'singiel' && index === 0) ||
                (gameState.opponentTeam.gameType === 'dublet' && index < 2) ||
                (gameState.opponentTeam.gameType === 'triplet');
              
              if (!shouldShow) return null;
              
              return (
                <div key={index}>
                  <label style={{
                    display: 'block',
                    fontFamily: 'Nunito, Arial, sans-serif',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    color: '#c62828',
                    marginBottom: '5px'
                  }}>
                    {getTranslation('gracz', language)} {index + 1}:
                  </label>
                  <input
                    type="text"
                    value={player}
                    onChange={(e) => handlePlayerNameChange('opponentTeam', index, e.target.value)}
                    placeholder={`${getTranslation('wprowadzImieGracza', language)} ${index + 1}`}
                    maxLength={30}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: `2px solid ${
                        isPlayerFieldValid(gameState.opponentTeam, index) 
                          ? '#4caf50' 
                          : '#f44336'
                      }`,
                      borderRadius: '8px',
                      fontSize: '1rem',
                      fontFamily: 'Nunito, Arial, sans-serif',
                      outline: 'none',
                      transition: 'border-color 0.2s ease',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = isPlayerFieldValid(gameState.opponentTeam, index) ? '#4caf50' : '#f44336';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = isPlayerFieldValid(gameState.opponentTeam, index) ? '#4caf50' : '#f44336';
                    }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Start Game Button */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        marginTop: '30px',
        padding: '0 20px'
      }}>
        <button
          onClick={handleStartGame}
          disabled={!validateTeams()}
          style={{
            background: validateTeams() 
              ? 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)'
              : 'linear-gradient(135deg, #bdbdbd 0%, #9e9e9e 100%)',
            color: 'white',
            border: 'none',
            padding: '15px 30px',
            borderRadius: '12px',
            fontSize: '1.2rem',
            fontWeight: '600',
            cursor: validateTeams() ? 'pointer' : 'not-allowed',
            transition: 'transform 0.2s ease',
            fontFamily: 'Nunito, Arial, sans-serif',
            boxShadow: validateTeams() ? '0 4px 12px rgba(0, 0, 0, 0.15)' : '0 2px 6px rgba(0, 0, 0, 0.1)',
            opacity: validateTeams() ? 1 : 0.7
          }}
          onMouseOver={(e) => {
            if (validateTeams()) {
              e.target.style.transform = 'translateY(-2px)';
            }
          }}
          onMouseOut={(e) => {
            if (validateTeams()) {
              e.target.style.transform = 'translateY(0)';
            }
          }}
        >
          {validateTeams() ? getTranslation('rozpocznijMecz', language) : getTranslation('wypelnijPola', language)}
        </button>
      </div>
        </>
      ) : (
        /* Results View */
        <>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'flex-start', 
          minHeight: '50vh',
          gap: '20px',
          padding: '0 20px',
          flexWrap: 'nowrap'
        }}>
          {/* {getTranslation('moja-druzyna-section', language)} Section */}
          <div style={{
            background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
            padding: '30px',
            borderRadius: '16px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            minWidth: '100px',
            maxWidth: '150px',
            flex: '1',
            height: '250px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
          }}>
            <h2 style={{
              fontFamily: 'Nunito, Arial, sans-serif',
              fontWeight: 600,
              fontSize: '1.2rem',
              color: '#1565c0',
              margin: '0 0 20px 0',
              textAlign: 'center'
            }}>
              {getTranslation('twojaDruzyna', language)}
            </h2>
            
            {/* Points Counter */}
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              gap: '10px'
            }}>
              <button
                onClick={() => handlePointsChange('yourTeam', 1)}
                disabled={gameState.yourTeam.points >= 13}
                style={{
                  background: gameState.yourTeam.points >= 13 
                    ? 'linear-gradient(135deg, #bdbdbd 0%, #9e9e9e 100%)'
                    : 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                  color: 'white',
                  border: 'none',
                  width: '60px',
                  height: '40px',
                  borderRadius: '8px',
                  fontSize: '24px',
                  fontWeight: 'bold',
                  cursor: gameState.yourTeam.points >= 13 ? 'not-allowed' : 'pointer',
                  transition: 'transform 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseOver={(e) => {
                  if (gameState.yourTeam.points < 13) {
                    e.target.style.transform = 'translateY(-2px)';
                  }
                }}
                onMouseOut={(e) => {
                  if (gameState.yourTeam.points < 13) {
                    e.target.style.transform = 'translateY(0)';
                  }
                }}
              >
                +
              </button>
              
              <div style={{
                fontSize: '3rem',
                fontWeight: 'bold',
                color: '#1565c0',
                fontFamily: 'Nunito, Arial, sans-serif',
                minWidth: '80px',
                textAlign: 'center'
              }}>
                {gameState.yourTeam.points}
              </div>
              
              <button
                onClick={() => handlePointsChange('yourTeam', -1)}
                disabled={gameState.yourTeam.points <= 0}
                style={{
                  background: gameState.yourTeam.points <= 0 
                    ? 'linear-gradient(135deg, #bdbdbd 0%, #9e9e9e 100%)'
                    : 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                  color: 'white',
                  border: 'none',
                  width: '60px',
                  height: '40px',
                  borderRadius: '8px',
                  fontSize: '24px',
                  fontWeight: 'bold',
                  cursor: gameState.yourTeam.points <= 0 ? 'not-allowed' : 'pointer',
                  transition: 'transform 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseOver={(e) => {
                  if (gameState.yourTeam.points > 0) {
                    e.target.style.transform = 'translateY(-2px)';
                  }
                }}
                onMouseOut={(e) => {
                  if (gameState.yourTeam.points > 0) {
                    e.target.style.transform = 'translateY(0)';
                  }
                }}
              >
                -
              </button>
            </div>
          </div>

          {/* Przeciwnicy Section */}
          <div style={{
            background: 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)',
            padding: '30px',
            borderRadius: '16px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            minWidth: '100px',
            maxWidth: '150px',
            flex: '1',
            height: '250px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
          }}>
            <h2 style={{
              fontFamily: 'Nunito, Arial, sans-serif',
              fontWeight: 600,
              fontSize: '1.2rem',
              color: '#c62828',
              margin: '0 0 20px 0',
              textAlign: 'center'
            }}>
              {getTranslation('druzynaPrzeciwna', language)}
            </h2>
            
            {/* Points Counter */}
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              gap: '10px'
            }}>
              <button
                onClick={() => handlePointsChange('opponentTeam', 1)}
                disabled={gameState.opponentTeam.points >= 13}
                style={{
                  background: gameState.opponentTeam.points >= 13 
                    ? 'linear-gradient(135deg, #bdbdbd 0%, #9e9e9e 100%)'
                    : 'linear-gradient(135deg, #d32f2f 0%, #c62828 100%)',
                  color: 'white',
                  border: 'none',
                  width: '60px',
                  height: '40px',
                  borderRadius: '8px',
                  fontSize: '24px',
                  fontWeight: 'bold',
                  cursor: gameState.opponentTeam.points >= 13 ? 'not-allowed' : 'pointer',
                  transition: 'transform 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseOver={(e) => {
                  if (gameState.opponentTeam.points < 13) {
                    e.target.style.transform = 'translateY(-2px)';
                  }
                }}
                onMouseOut={(e) => {
                  if (gameState.opponentTeam.points < 13) {
                    e.target.style.transform = 'translateY(0)';
                  }
                }}
              >
                +
              </button>
              
              <div style={{
                fontSize: '3rem',
                fontWeight: 'bold',
                color: '#c62828',
                fontFamily: 'Nunito, Arial, sans-serif',
                minWidth: '80px',
                textAlign: 'center'
              }}>
                {gameState.opponentTeam.points}
              </div>
              
              <button
                onClick={() => handlePointsChange('opponentTeam', -1)}
                disabled={gameState.opponentTeam.points <= 0}
                style={{
                  background: gameState.opponentTeam.points <= 0 
                    ? 'linear-gradient(135deg, #bdbdbd 0%, #9e9e9e 100%)'
                    : 'linear-gradient(135deg, #d32f2f 0%, #c62828 100%)',
                  color: 'white',
                  border: 'none',
                  width: '60px',
                  height: '40px',
                  borderRadius: '8px',
                  fontSize: '24px',
                  fontWeight: 'bold',
                  cursor: gameState.opponentTeam.points <= 0 ? 'not-allowed' : 'pointer',
                  transition: 'transform 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseOver={(e) => {
                  if (gameState.opponentTeam.points > 0) {
                    e.target.style.transform = 'translateY(-2px)';
                  }
                }}
                onMouseOut={(e) => {
                  if (gameState.opponentTeam.points > 0) {
                    e.target.style.transform = 'translateY(0)';
                  }
                }}
              >
                -
              </button>
            </div>
          </div>
        </div>

        {/* Save Game Button */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          marginTop: '30px',
          padding: '0 20px'
        }}>
          <button
            onClick={handleSaveGame}
            disabled={isLoading}
            style={{
              background: isLoading 
                ? 'linear-gradient(135deg, #bdbdbd 0%, #9e9e9e 100%)'
                : 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
              color: 'white',
              border: 'none',
              padding: '15px 30px',
              borderRadius: '12px',
              fontSize: '1.2rem',
              fontWeight: '600',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              transition: 'transform 0.2s ease',
              fontFamily: 'Nunito, Arial, sans-serif',
              boxShadow: isLoading ? '0 2px 6px rgba(0, 0, 0, 0.1)' : '0 4px 12px rgba(0, 0, 0, 0.15)',
              opacity: isLoading ? 0.7 : 1
            }}
            onMouseOver={(e) => {
              if (!isLoading) {
                e.target.style.transform = 'translateY(-2px)';
              }
            }}
            onMouseOut={(e) => {
              if (!isLoading) {
                e.target.style.transform = 'translateY(0)';
              }
            }}
          >
            {isLoading ? getTranslation('zapisywanie', language) : getTranslation('zapisz', language)}
          </button>
        </div>

        </>
      )}

      {/* Error display */}
      {error && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: '20px',
          padding: '0 20px'
        }}>
          <div style={{
            background: '#ffebee',
            color: '#c62828',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid #ffcdd2',
            maxWidth: '600px',
            width: '100%',
            textAlign: 'center',
            fontFamily: 'Nunito, Arial, sans-serif'
          }}>
            {error}
          </div>
        </div>
      )}

      {/* Loading indicator */}
      {isLoading && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: '20px',
          padding: '0 20px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            fontFamily: 'Nunito, Arial, sans-serif',
            fontSize: '1.1rem',
            color: '#1e3a8a'
          }}>
            <div style={{
              width: '20px',
              height: '20px',
              border: '2px solid #e3f2fd',
              borderTop: '2px solid #1e3a8a',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            Ładowanie...
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '40px 30px',
            maxWidth: '400px',
            width: '100%',
            textAlign: 'center',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
            fontFamily: 'Nunito, Arial, sans-serif'
          }}>
            {/* Success Icon */}
            <div style={{
              width: '80px',
              height: '80px',
              backgroundColor: '#10b981',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
              fontSize: '40px',
              color: 'white'
            }}>
              ✓
            </div>
            
            {/* Success Message */}
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              color: '#1f2937',
              margin: '0 0 16px 0',
              lineHeight: '1.3'
            }}>
              {getTranslation('gra-zostala-pomyslnie-zapisana', language)}
            </h2>
            
            <p style={{
              fontSize: '1rem',
              color: '#6b7280',
              margin: '0 0 32px 0',
              lineHeight: '1.5'
            }}>
              {getTranslation('wyniki-meczu-zapisane', language)}
            </p>
            
            {/* OK Button */}
            <button
              onClick={handleSuccessModalClose}
              style={{
                background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                color: 'white',
                border: 'none',
                padding: '14px 32px',
                borderRadius: '12px',
                fontSize: '1.1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                fontFamily: 'Nunito, Arial, sans-serif',
                minWidth: '120px'
              }}
              onMouseOver={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 8px 20px rgba(124, 58, 237, 0.3)';
              }}
              onMouseOut={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* CSS for loading animation */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default FriendlyGame;
